import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import _ from 'underscore';

import { useActions } from 'hooks/useActions';
import * as actions from 'pages/CommunitySelectedPage/actions';
import { getCourseAccessLevelInformation, getFirstIncompleteLevel } from 'functions/getCourseExerciseInformation';
import CourseFlowOverlay from 'components/CourseFlowOverlay/CourseFlowOverlay';
import FreecodeAuthModal from 'components/Modals/FreecodeModals/FreecodeAuthModal/FreecodeAuthModal';
import CourseSignupModal from './components/CourseSignUpModal';
import PurchaseCourseModal from './components/PurchaseCourseModal';
import CourseModule from './components/CourseModule';
import CourseSyllabus from './components/CourseSyllabus';
import Dropdown from 'components/Dropdown/Dropdown';
import { getCookie } from 'functions/getCookie';
import { getConfig } from 'functions/getConfig';
import { contestLanguages } from 'constants/languages';
import { CommunityTags } from 'components/Tags/Tags';
import { SectionContainer, Title, TagsContainer } from 'components/CommunityPageImageAndDescription/styles';
import { Wrapper, Container } from 'components/Layout/Layout';
import {
  DescriptionAndContentContainer,
  DescriptionContainer,
  ContentContainer,
  DescriptionHeading,
  Description,
  DropdownContainer,
  LanguageLabel,
} from './styles';

const config = getConfig();

export default function CoursePage() {
  const { id } = useParams();

  const { user } = useSelector((state) => state.auth);
  const { selectedItem: course, inputExercise, error } = useSelector((state) => state.CommunitySelectedPageReducer);

  const { getCourse, updateFlowProgress, resetInputExerciseProgress } = useActions(actions);

  const [description, setDescription] = useState('');
  const [language, setLanguage] = useState('python');
  const [showFlowOverlay, setShowFlowOverlay] = useState(false);
  const [courseFlow, setCourseFlow] = useState(false);
  const [defaultInputExercise, setDefaultInputExercise] = useState({});
  const [displayAuthModal, setDisplayAuthModal] = useState(false);
  const [displayPurchaseModal, setDisplayPurchaseModal] = useState(false);
  const [displayCourseSignUpModal, setDisplayCourseSignUpModal] = useState(false);
  const [freeCount, setFreeCount] = useState(0);
  const [signInExercises, setSignInExercises] = useState([]);
  const [firstIncompleteLevel, setFirstIncompleteLevel] = useState({
    syllabusIndex: 0,
    moduleIndex: 0,
    levelIndex: 0,
  });

  useEffect(() => {
    if (!course?._id) return;
    setDescription(course.description.replaceAll('<p>', '<p style="word-break: break-word;">'));
    setLanguage(getCookie(`fsCourse-${course._id}-language`) ?? course.lastUsedLanguage ?? 'python');
  }, [course]);

  useEffect(() => {
    if (id) getCourse(id);
  }, [id, getCourse]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (user?._id && displayAuthModal) {
      setDisplayAuthModal(false);
      getCourse(course._id);
    }
  }, [user, course._id, getCourse, displayAuthModal]);

  useEffect(() => {
    let hasCourseCookies =
      getCookie(`fsCourse-${course._id}-language`) || getCookie(`fsCourse-${course._id}-levelToRun`);
    if (
      hasCourseCookies &&
      course.syllabus[firstIncompleteLevel.syllabusIndex] &&
      firstIncompleteLevel.syllabusIndex > 0
    ) {
      let element = document.getElementById(
        `module-${firstIncompleteLevel.syllabusIndex}-${firstIncompleteLevel.moduleIndex}`
      );
      let elementPosition = element.getBoundingClientRect().top;
      let offsetPosition = elementPosition + window.scrollY - 140;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [firstIncompleteLevel, course]);

  // It finds which level is not completed of the selected language
  const updateFirstIncompleteLevel = useCallback(() => {
    if (!user._id || !course?._id) return;

    let { syllabusIndex, moduleIndex, levelIndex } = getFirstIncompleteLevel(course, language);
    setFirstIncompleteLevel({ syllabusIndex, moduleIndex, levelIndex });
  }, [course, language, user._id]);

  useEffect(() => {
    if (!course?._id) return;
    const { freeCount, signInExercises } = getCourseAccessLevelInformation(course);
    setFreeCount(freeCount);
    setSignInExercises(signInExercises);
  }, [course]);

  useEffect(() => {
    updateFirstIncompleteLevel();
  }, [updateFirstIncompleteLevel]);

  const checkAndInitializeFlow = (event, exerciseId, levelIndex, testImpl, inputExercise) => {
    localStorage.setItem(`fsEditor-exercise-${exerciseId}-levelToRun`, String(levelIndex));
    document.cookie = `fsCourse-${course._id}-levelToRun=${levelIndex + 1}; Domain=${
      config.COOKIE
    }; Max-Age=100; Path=/; SameSite=Lax`;
    event.stopPropagation();
    if (testImpl.parsedFlow?.[levelIndex]) {
      setCourseFlow({
        levelIndexStart: levelIndex,
        courseId: course._id,
        testImpl: testImpl,
        language: language === 'any' ? 'python' : language,
      });
      setDefaultInputExercise(inputExercise);
      setShowFlowOverlay(true);
    } else {
      window.location.href = `/exercise/${exerciseId}?courseId=${course._id}`;
    }
  };

  const handleClickOnLevelName = (event, exerciseId, levelIndex, testImpl, inputExercise, exerciseAccessLevel) => {
    if (!course.isPaidCourse) return checkAndInitializeFlow(event, exerciseId, levelIndex, testImpl, inputExercise);
    if (!user?._id && ['paid', 'signedIn'].includes(exerciseAccessLevel)) return setDisplayCourseSignUpModal(true);
    if (exerciseAccessLevel === 'paid' && user?._id) return setDisplayPurchaseModal(true);
    else checkAndInitializeFlow(event, exerciseId, levelIndex, testImpl, inputExercise);
  };

  let languageOptions = useMemo(() => {
    let arr = [];
    let contestLanguagesByValue = _.indexBy(contestLanguages, 'value');
    for (const language in course.languages) {
      arr.push({
        label: course.languages[language].SimpleName,
        value: language,
        icon: contestLanguagesByValue[language].icon,
      });
    }
    arr.push({ label: 'Any Language', value: 'any' });
    return arr;
  }, [course]);
  let languageOptionsByValue = useMemo(() => _.indexBy(languageOptions, 'value'), [languageOptions]);

  return (
    <Wrapper isNewPage>
      <Container column>
        <DescriptionAndContentContainer>
          <DescriptionContainer>
            <Title>{course?.name}</Title>
            <TagsContainer isCoursePage>
              <CommunityTags size={12} tags={course.keywords ?? []} singleLine />
            </TagsContainer>
            <SectionContainer>
              <DescriptionHeading>Description</DescriptionHeading>
              <Description dangerouslySetInnerHTML={{ __html: description }} isCoursePage />
            </SectionContainer>
            {course._id && (
              <CourseSyllabus
                key={course._id}
                course={course}
                user={user}
                language={language}
                handleClickOnLevelName={handleClickOnLevelName}
              />
            )}
          </DescriptionContainer>
          {course._id && (
            <>
              <ContentContainer>
                <LanguageLabel>Choose you language:</LanguageLabel>
                <DropdownContainer>
                  <Dropdown
                    icon={languageOptionsByValue[language].icon}
                    value={languageOptionsByValue[language].label}
                    options={languageOptions}
                    width="225px"
                    onChange={(selected) => {
                      setLanguage(selected.value);
                      document.cookie = `fsCourse-${course._id}-language=${selected.value}; Domain=${config.COOKIE}; Max-Age=604800; Path=/; SameSite=Lax`;
                    }}
                  />
                </DropdownContainer>
                <CourseModule
                  key={course._id}
                  user={user}
                  course={course}
                  language={language}
                  firstIncompleteLevel={firstIncompleteLevel}
                  handleClickOnLevelName={handleClickOnLevelName}
                  setDisplayCourseSignUpModal={setDisplayCourseSignUpModal}
                />
              </ContentContainer>
            </>
          )}
        </DescriptionAndContentContainer>
      </Container>

      {showFlowOverlay && (
        <CourseFlowOverlay
          courseFlow={{ ...courseFlow }}
          inputExercise={
            inputExercise._id
              ? inputExercise
              : defaultInputExercise._id
                ? defaultInputExercise
                : JSON.parse(
                    localStorage.getItem(`fs-flow-progress-${courseFlow.testImpl.test}`) ??
                      JSON.stringify({ levelData: {} })
                  )
          }
          updateFlowProgress={(data) => updateFlowProgress(user._id, data)}
          close={() => {
            updateFirstIncompleteLevel();
            window.customPixiComponentForFlow = {};
            resetInputExerciseProgress();
            getCourse(course._id);
            setShowFlowOverlay(false);
          }}
        />
      )}
      {displayAuthModal && <FreecodeAuthModal onCancel={() => setDisplayAuthModal(!displayAuthModal)} />}
      {displayPurchaseModal && <PurchaseCourseModal course={course} close={() => setDisplayPurchaseModal(false)} />}
      {displayCourseSignUpModal && (
        <CourseSignupModal
          list={[
            `First ${freeCount} modules`,
            `${signInExercises.join(', ')} ${signInExercises.length > 1 ? 'are' : 'is'} available if you sign up`,
          ]}
          onClose={() => setDisplayCourseSignUpModal(false)}
          onSignUp={() => {
            setDisplayCourseSignUpModal(false);
            setDisplayAuthModal(true);
          }}
        />
      )}
    </Wrapper>
  );
}
